import NodeTypeChip from "@components/NodeTypeChip"
import { AppNode, NodeType } from "@features/builder/graph/types"
import { LabelField } from "@features/builder/NodeDrawer/NodeForm/LabelField"
import { LagLabelField } from "@features/builder/NodeDrawer/NodeForm/LagLabelField"
import { DECIMAL_PLACES_OPTIONS } from "@features/builder/NodeDrawer/NodeForm/UnitFields/UnitField"
import { BulkNodesEditFormValues } from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/BulkNodesEditForm"
import { Clear } from "@mui/icons-material"
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import HistoricMnemonicField from "@components/HistoricMnemonicField"
import ScenarioMnemonicField from "@components/ScenarioMnemonicField"
import ForecastMnemonicField from "@components/ForecastMnemonicField"
import React, { memo } from "react"
import TableFieldCell from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/TableFieldCell"
import NodeLinkField from "@components/NodeLink/NodeLinkField"
import { LINKABLE_NODE_TYPES, isTaggableNodeType } from "@features/builder/helpers/nodes"
import ValidatedMnemonicField from "@features/builder/ValidatedMnemonicField"
import NodeTagsCell from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/NodeTagsCell"
import GraphTagsCell from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/GraphTagsCell"
import {
  STICKY_COLUMN_LEFT,
  getStickyColumnSx,
} from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/stickyColumns"

type BulkNodesEditFormRowProps = {
  node: AppNode
  numericOrder: number
}

const BulkNodesEditFormRow = (props: BulkNodesEditFormRowProps) => {
  const rowPath = `nodes.${props.node.id}` as const
  const form = useFormContext<BulkNodesEditFormValues>()
  const nodeLink = useWatch({
    control: form.control,
    name: `${rowPath}.link`,
  })
  const hasLink = Boolean(nodeLink)

  const handleRemoveLink = (nodeId: string) => {
    form.setValue(`nodes.${nodeId}.link`, "", { shouldDirty: true })
  }

  const isLagNodeWithExpression =
    props.node.type === NodeType.Lag &&
    props.node.data.originalNode.properties.df_cpd.expression?.[0] !== ""
  const isGroupNode = props.node.type === NodeType.Group

  return (
    <TableRow sx={{ ".MuiTableCell-root": { p: 1 } }}>
      <TableCell sx={getStickyColumnSx(STICKY_COLUMN_LEFT.numericOrder)}>
        <strong>{props.numericOrder}.</strong>
      </TableCell>

      <TableCell align="center" sx={getStickyColumnSx(STICKY_COLUMN_LEFT.isSelected)}>
        <Controller
          name={`${rowPath}.isSelected`}
          control={form.control}
          render={({ field: { value, ...field } }) => (
            <FormControlLabel
              sx={{ m: 0 }}
              label=""
              control={<Checkbox {...field} checked={value} />}
            />
          )}
        />
      </TableCell>

      <TableFieldCell
        nodeId={props.node.id}
        fieldName="label"
        sx={getStickyColumnSx(STICKY_COLUMN_LEFT.label)}
      >
        {props.node.type === NodeType.Lag ? (
          <LagLabelField
            control={form.control}
            name={`${rowPath}.label`}
            lagValueFieldName={`${rowPath}.lagValue`}
            label=""
          />
        ) : (
          <LabelField control={form.control} name={`${rowPath}.label`} label="" />
        )}
      </TableFieldCell>

      <TableCell>
        <NodeTypeChip type={props.node.type} />
      </TableCell>

      <TableFieldCell nodeId={props.node.id} fieldName="link">
        {LINKABLE_NODE_TYPES.includes(props.node.type) && (
          <NodeLinkField
            isFormDisabled={form.formState.disabled}
            node={props.node}
            control={form.control}
            name={`${rowPath}.link`}
            onRemove={handleRemoveLink}
            limitChipWidth={true}
            label=""
          />
        )}
      </TableFieldCell>

      <TableFieldCell nodeId={props.node.id} fieldName="unitShowAsPercentage" align="center">
        {!isGroupNode && (
          <Controller
            name={`${rowPath}.unitShowAsPercentage`}
            control={form.control}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                sx={{ m: 0 }}
                label=""
                control={
                  <Checkbox {...field} disabled={field.disabled || hasLink} checked={value} />
                }
              />
            )}
          />
        )}
      </TableFieldCell>

      <TableFieldCell nodeId={props.node.id} fieldName="unitDecimalPlaces">
        {!isGroupNode && (
          <>
            <Controller
              control={form.control}
              name={`${rowPath}.unitDecimalPlaces`}
              render={({ field, fieldState }) => (
                <TextField
                  size="small"
                  fullWidth
                  helperText={fieldState.error?.message}
                  error={Boolean(fieldState.error)}
                  {...field}
                  disabled={field.disabled || hasLink}
                  select
                  sx={{ ".MuiSelect-icon": { display: "none" } }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        sx={{ display: !field.value ? "none" : "inline-flex" }}
                        onClick={() => {
                          form.setValue(`${rowPath}.unitDecimalPlaces`, "", {
                            shouldDirty: true,
                          })
                        }}
                        disabled={field.disabled || hasLink}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    ),
                  }}
                >
                  {DECIMAL_PLACES_OPTIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </>
        )}
      </TableFieldCell>

      <TableFieldCell nodeId={props.node.id} fieldName="unitSuffix">
        {!isGroupNode && (
          <Controller
            name={`${rowPath}.unitSuffix`}
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                size="small"
                fullWidth
                helperText={fieldState.error?.message}
                error={Boolean(fieldState.error)}
                {...field}
                disabled={field.disabled || hasLink}
              />
            )}
          />
        )}
      </TableFieldCell>

      <TableFieldCell nodeId={props.node.id} fieldName="historicMnemonic">
        {!isGroupNode && (
          <Tooltip
            title={
              isLagNodeWithExpression
                ? "Cannot edit historic mnemonic for lag node with expression"
                : ""
            }
          >
            <span>
              <ValidatedMnemonicField nodeId={props.node.id} errorsKey="HISTORIC_MNEMONIC">
                <HistoricMnemonicField
                  control={form.control}
                  name={`${rowPath}.historicMnemonic`}
                  disabled={isLagNodeWithExpression || hasLink}
                />
              </ValidatedMnemonicField>
            </span>
          </Tooltip>
        )}
      </TableFieldCell>

      <TableFieldCell nodeId={props.node.id} fieldName="scenarioMnemonic">
        {props.node.type === NodeType.Scenario && (
          <ValidatedMnemonicField nodeId={props.node.id} errorsKey="SCENARIO_MNEMONIC">
            <ScenarioMnemonicField control={form.control} name={`${rowPath}.scenarioMnemonic`} />
          </ValidatedMnemonicField>
        )}
      </TableFieldCell>

      <TableFieldCell nodeId={props.node.id} fieldName="forecastMnemonic">
        {props.node.type === NodeType.Forecast && (
          <ValidatedMnemonicField nodeId={props.node.id} errorsKey="FORECAST_MNEMONIC">
            <ForecastMnemonicField control={form.control} name={`${rowPath}.forecastMnemonic`} />
          </ValidatedMnemonicField>
        )}
      </TableFieldCell>

      <TableFieldCell nodeId={props.node.id} fieldName="tags">
        {isTaggableNodeType(props.node.type) && !hasLink && <NodeTagsCell nodeId={props.node.id} />}
      </TableFieldCell>

      <TableFieldCell nodeId={props.node.id} fieldName="graphTags">
        {isTaggableNodeType(props.node.type) && !hasLink && (
          <GraphTagsCell nodeId={props.node.id} />
        )}
      </TableFieldCell>
    </TableRow>
  )
}

const MemoizedBulkNodesEditFormRow = memo(
  BulkNodesEditFormRow,
  (prevProps, nextProps) => prevProps.node === nextProps.node,
)

MemoizedBulkNodesEditFormRow.displayName = "BulkNodesEditFormRow"

export default MemoizedBulkNodesEditFormRow
